import { jwtDecode } from "jwt-decode";
import {
  createBrowserRouter,
  LoaderFunctionArgs,
  redirect,
  RouteObject,
} from "react-router-dom";
import AccountRecoveryPage from "./components/AccountRecoveryPage";
import CompanyActivationPage from "./components/CompanyActivationPage";
import AdminPage from "./components/AdminPage";
import App from "./components/App";
import ConversationPage from "./components/ConversationPage";
import CreateOilRequestPage from "./components/CreateOilRequestPage";
import IndexPage from "./components/IndexPage";
import InformationPage from "./components/InformationPage";
import OilRequestPage from "./components/OilRequestPage";
import OilRequestsPage from "./components/OilRequestsPage";
import SettingsPage from "./components/SettingsPage";
import SignInPage from "./components/SignInPage";
import SignUpPage from "./components/SignUpPage";
import { getAuthenticationToken } from "./utils/user";
import CustomerStoriesPage from "./components/CustomerStoriesPage";
import TermsAndConditionsPage from "./components/TermsAndConditionsPage";
import StatisticsPage from "./components/StatisticsPage";
import MarketInfoPage from "./components/MarketInfoPage";
import FuelTanksPage from "./components/FuelTanksPage";

function anonymousLoader({ request }: LoaderFunctionArgs) {
  const fakeAuthProvider = {
    isAuthenticated: getAuthenticationToken() !== null,
  };
  if (fakeAuthProvider.isAuthenticated) {
    return redirect("/oil-requests");
  }
  return null;
}

function clientLoader({ request }: LoaderFunctionArgs) {
  const fakeAuthProvider = {
    isAuthenticated: getAuthenticationToken() !== null,
  };
  // If the user is not logged in and tries to access `/protected`, we redirect
  // them to `/login` with a `from` parameter that allows login to redirect back
  // to this page upon successful authentication
  if (!fakeAuthProvider.isAuthenticated) {
    const url = new URL(request.url);
    return redirect("/signin?redirectTo=" + encodeURIComponent(url.pathname));
  }
  return null;
}

function adminLoader({ request }: LoaderFunctionArgs) {
  const fakeAuthProvider = {
    isAuthenticated: getAuthenticationToken() !== null,
  };
  // If the user is not logged in and tries to access `/protected`, we redirect
  // them to `/login` with a `from` parameter that allows login to redirect back
  // to this page upon successful authentication
  if (!fakeAuthProvider.isAuthenticated) {
    const url = new URL(request.url);
    return redirect("/signin?redirectTo=" + encodeURIComponent(url.pathname));
  }

  const decodedToken = jwtDecode<{ id: string; exp: number; role: string }>(
    getAuthenticationToken(),
  );
  if (decodedToken.role !== "Admin") {
    return redirect("/oil-requests");
  }

  return null;
}

const routes: RouteObject[] = [
  {
    path: "/",
    Component: App,
    children: [
      {
        path: "/",
        Component: IndexPage,
        loader: anonymousLoader,
      },
      {
        path: "/advertisement",
        element: (
          <InformationPage>
            <InformationPage.Advertisement />
          </InformationPage>
        ),
        loader: anonymousLoader,
      },
      {
        path: "/frequently-asked-questions",
        element: (
          <InformationPage>
            <InformationPage.FrequentlyAskedQuestions />
          </InformationPage>
        ),
        loader: anonymousLoader,
      },
      {
        path: "/instructions",
        element: (
          <InformationPage>
            <InformationPage.Instructions />
          </InformationPage>
        ),
        loader: anonymousLoader,
      },
      {
        path: "/companies",
        Component: CompanyActivationPage,
      },
      {
        path: "/account-recovery",
        Component: AccountRecoveryPage,
        loader: anonymousLoader,
      },
      {
        path: "/signin",
        Component: SignInPage,
        loader: anonymousLoader,
      },
      {
        path: "/signup",
        Component: SignUpPage,
        loader: anonymousLoader,
      },
      {
        path: "/article/:locale/:id?/:title?",
        Component: CustomerStoriesPage,
        loader: anonymousLoader,
      },
      {
        path: "/terms-and-conditions",
        element: (
          <InformationPage>
            <InformationPage.TermsAndConditions />
          </InformationPage>
        ),
        loader: anonymousLoader,
      },
      {
        path: "/privacy-policy",
        element: (
          <InformationPage>
            <InformationPage.PrivacyPolicy />
          </InformationPage>
        ),
        loader: anonymousLoader,
      },
      {
        path: "/settings",
        Component: SettingsPage,
        loader: clientLoader,
      },
      {
        path: "/statistics",
        Component: StatisticsPage,
        loader: clientLoader,
      },
      {
        path: "/market-info",
        Component: MarketInfoPage,
        loader: clientLoader,
      },
      {
        path: "/fuel-tanks",
        Component: FuelTanksPage,
        loader: clientLoader,
      },
      {
        path: "/oil-requests",
        Component: OilRequestsPage,
        loader: clientLoader,
      },
      {
        path: "/oil-requests/create",
        Component: CreateOilRequestPage,
        loader: clientLoader,
      },
      {
        path: "/oil-requests/:id",
        Component: OilRequestPage,
        loader: clientLoader,
      },
      {
        path: "/conversations",
        Component: ConversationPage,
        loader: clientLoader,
      },
      {
        path: "/conversations/:id",
        Component: ConversationPage,
        loader: clientLoader,
      },
      {
        path: "/terms-conditions",
        Component: TermsAndConditionsPage,
        loader: clientLoader,
      },
      {
        path: "/admin",
        Component: AdminPage,
        loader: adminLoader,
      },
    ],
  },
];

export default createBrowserRouter(routes);
